import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';

import { linkIDs } from '../../../../services/link-service/links';
import { useEnableBrandedSMS } from '../../../../feature-flags/hooks/use-enable-branded-sms';
import HelpArticleLink from '../../../../components/help-article-link/index';
import { useAreLocalDeliveryServicesAvailableForSeller } from '../../../../hooks/use-are-local-delivery-services-available-for-seller';
import { useBTEmailExperienceNew } from '../../../../hooks/use-seller-setting';
import { useIsBrandingTrackingEnabledForSeller } from '../../branding-defaults/use-is-branding-tracking-enabled-for-seller';
import { LockIconWithTooltip } from '../../../../feature-gates/lock-icon-with-tooltip';
import { useStoreUpdateContext } from './context';
import messages from './messages';

import style from './style.module.less';

const GROUP_NAME = 'brandingSettings';
const USE_BRANDED_TRACKING = 'useBrandedTrackingPage';
const SMS_IS_ENABLED = 'smsIsEnabled';

const TrackingPageTab = ({ brandingSettings }) => {
  const { updateStoreSetting } = useStoreUpdateContext();
  const { useBrandedTrackingPage, smsIsEnabled } = brandingSettings;
  const enableBrandedSMS = useEnableBrandedSMS();
  const enableNewEmailExperience = useBTEmailExperienceNew();
  const sellerHasLocalDeliveryServices = useAreLocalDeliveryServicesAvailableForSeller();
  const { hasBaseFeatures: hasBrandedTrackingFeature } = useIsBrandingTrackingEnabledForSeller();

  const utilizeTheBrandedTrackingPage = useCallback(() => updateStoreSetting(GROUP_NAME, USE_BRANDED_TRACKING, true), [updateStoreSetting]);
  const doNotUtilizeTheBrandedTrackingPage = useCallback(
    () => updateStoreSetting(GROUP_NAME, USE_BRANDED_TRACKING, false),
    [updateStoreSetting],
  );
  const enableSMSTracking = useCallback(() => updateStoreSetting(GROUP_NAME, SMS_IS_ENABLED, true), [updateStoreSetting]);
  const disableSMSTracking = useCallback(() => updateStoreSetting(GROUP_NAME, SMS_IS_ENABLED, false), [updateStoreSetting]);

  const isCarrierTrackingPageDisabled = !hasBrandedTrackingFeature;
  return (
    <div className={style['tab-content']}>
      <span className={style['tab-title']}>
        <FormattedMessage {...messages.trackingPageTab} />
      </span>
      <div className={style['tracking-page']}>
        <FormattedMessage {...(enableNewEmailExperience ? messages.trackingPageDescriptionNew : messages.trackingPageDescription)} />
      </div>
      <div className={style['radio-wrapper']}>
        <RadioCheckList className={style['radio-checklist']}>
          <RadioCheckListItem isChecked={useBrandedTrackingPage} onChecked={utilizeTheBrandedTrackingPage}>
            <FormattedMessage {...messages.brandedTrackingPage} />
          </RadioCheckListItem>
          <RadioCheckListItem
            isChecked={!useBrandedTrackingPage}
            isDisabled={isCarrierTrackingPageDisabled}
            onChecked={doNotUtilizeTheBrandedTrackingPage}
          >
            <div className={style['radio-content-disabled']}>
              <FormattedMessage {...messages.carrierTrackingPage} />
              {isCarrierTrackingPageDisabled && <LockIconWithTooltip />}
            </div>
          </RadioCheckListItem>
        </RadioCheckList>
        {sellerHasLocalDeliveryServices && (
          <div className={style['settings-disclaimer']}>
            <FormattedMessage {...messages.localDeliveryTrackingDisclaimer} />
          </div>
        )}
      </div>
      {enableBrandedSMS && (
        <>
          <h3 className={classnames(style['tab-title'], style['sms-notification-header'])}>
            <FormattedMessage {...messages.smsNotifications} />
          </h3>
          <div className={style['tracking-page']}>
            <FormattedMessage
              {...messages.smsNotificationDescription}
              values={{
                sms: (chunks) => (
                  <HelpArticleLink articleId={linkIDs.BrandedSMS} className={style['settings-link']}>
                    {chunks}
                  </HelpArticleLink>
                ),
                msglimits: (chunks) => (
                  <HelpArticleLink articleId={linkIDs.MessageLimitations} className={style['settings-link']}>
                    {chunks}
                  </HelpArticleLink>
                ),
              }}
            />
          </div>
          <div className={style['radio-wrapper']}>
            <RadioCheckList className={style['radio-checklist']}>
              <RadioCheckListItem isChecked={smsIsEnabled} isDisabled={!useBrandedTrackingPage} onChecked={enableSMSTracking}>
                <FormattedMessage {...messages.enableSMS} />
              </RadioCheckListItem>
              <RadioCheckListItem isChecked={!smsIsEnabled} isDisabled={!useBrandedTrackingPage} onChecked={disableSMSTracking}>
                <FormattedMessage {...messages.disableSMS} />
              </RadioCheckListItem>
            </RadioCheckList>
          </div>
        </>
      )}
    </div>
  );
};

TrackingPageTab.propTypes = {
  brandingSettings: PropTypes.object.isRequired,
};

export default TrackingPageTab;
